import React from 'react'
import { Theme, ColorTypes, useIsTheme } from '@afterpaytouch/core'
import {
  AU_PAY_MONTHLY_NAMESPACE,
  CONVERGENCE_NAMESPACE,
  NAMESPACE_SEPARATOR,
  THEME_COOKIE_NAME,
  UseCustomTranslationResponse,
  SetThemeRequest,
  LEGAL_PREFIX,
} from '../model'
import { useTranslation as useNextTranslation, UserConfig, SSRConfig } from 'next-i18next'
import { Trans as NextTrans } from 'next-i18next'
import { TransProps as NextTransProps } from 'react-i18next'
import { serverSideTranslations as originalServerSideTranslations } from 'next-i18next/serverSideTranslations'
import { setCookie } from 'nookies'
import { TOptions } from 'i18next'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { useCheckoutState, useCheckoutTheme, useCohort, useIsAuPayMonthly } from '../state'
import { Cohort } from '@afterpaytouch/portal-api'
import { useCallback, useEffect, useMemo } from 'react'
import { useMerchantCallback } from './merchant'

// Copy-variant namespace suffixes in lookup priority order. Variant files are optional per locale: a
// missing `<ns>__variant.json` falls through to the baseline `<ns>.json`.
const COPY_VARIANT_NAMESPACES = [AU_PAY_MONTHLY_NAMESPACE, CONVERGENCE_NAMESPACE]

export const generateI18nNamespace = (input: string[]): string[] => {
  return input.flatMap((item) => [item, ...COPY_VARIANT_NAMESPACES.map((variant) => `${item}${variant}`)])
}

// The one place session state (cohort, feature flags) is mapped to copy variants.
export const useActiveCopyVariants = (): string[] => {
  const cohort = useCohort()
  const isAuPayMonthly = useIsAuPayMonthly()
  return useMemo(() => {
    const active: string[] = []
    if (isAuPayMonthly) active.push(AU_PAY_MONTHLY_NAMESPACE)
    if (cohort === Cohort.CashOnly) active.push(CONVERGENCE_NAMESPACE)
    return active
  }, [cohort, isAuPayMonthly])
}

/**
 * Prepends each active copy-variant namespace to a `<ns>:<key>` key so i18next tries the overrides
 * first, in `variants` order, and falls back to the baseline key. Only the first separator marks the
 * namespace, matching i18next; keys with no namespace are returned untouched.
 *
 * Note: this only rewrites the key passed to `t()`. A `$t(ns:key)` nested inside a translation string
 * is resolved by i18next itself and always hits the baseline namespace, so variant files that need
 * variant text inside a nested reference must name it explicitly, e.g. `$t(common__cash:brandName)`.
 * @param key
 * @param variants namespace suffixes, e.g. ['__auPayMonthly', '__cash']
 * @returns
 */
export const getTranslationKeys = (key: string | string[], variants: string[] = []): string | string[] => {
  const processSingleKey = (originalKey: string): string | string[] => {
    if (variants.length === 0) {
      return originalKey
    }

    const separatorIndex = originalKey.indexOf(NAMESPACE_SEPARATOR)
    if (separatorIndex <= 0) {
      return originalKey
    }

    const translationNamespace = originalKey.slice(0, separatorIndex)
    const translationKey = originalKey.slice(separatorIndex + 1)

    const variantKeys = variants.map((variant) => `${translationNamespace}${variant}:${translationKey}`)

    return [...variantKeys, originalKey]
  }

  if (Array.isArray(key)) {
    const processedKeys = key.reduce<Array<string | string[]>>((acc, cur) => {
      const processed = processSingleKey(cur)
      return Array.isArray(processed) ? [...acc, ...processed] : [...acc, processed]
    }, [])
    return processedKeys.flat()
  } else {
    return processSingleKey(key)
  }
}

// Note: this custom useTranslation hook generate additional translation key for convergence
// If you need to use diff copy for Cohort.CashOnly, for eg: summary__cash.json, please use this hook
// If the options contain { legal: true }, the key will be prefixed with |░legal░| to find the legal translation

export const useTranslation = (ns?: string[]): UseCustomTranslationResponse => {
  const variants = useActiveCopyVariants()
  const { t: originalTFunction, i18n, ready } = useNextTranslation(ns)

  const enrichedT = useCallback(
    ((originalKey: string | string[], ...rest: unknown[]) => {
      const maybeOptions = rest.length > 0 ? rest[rest.length - 1] : undefined
      const options = typeof maybeOptions === 'object' && maybeOptions !== null ? (maybeOptions as TOptions & { legal?: boolean }) : undefined

      // If legal: true is set, prepend the legal prefix to the key(s)
      const applyLegalPrefix = (key: string): string => (options?.legal ? `${LEGAL_PREFIX}${key}` : key)
      const keyWithLegalPrefix = Array.isArray(originalKey) ? originalKey.map(applyLegalPrefix) : applyLegalPrefix(originalKey)

      const convergenceKeys = getTranslationKeys(keyWithLegalPrefix, variants)

      // i18next ignores the fallback keys when returnObjects is true and the first key is missing, so
      // pick the single best key ourselves: the first variant that has it, else the baseline.
      const resolvedKeys =
        options?.returnObjects && Array.isArray(convergenceKeys)
          ? convergenceKeys.find((candidate) => i18n.exists(candidate)) ?? convergenceKeys[convergenceKeys.length - 1]
          : convergenceKeys

      return (originalTFunction as unknown as (...args: unknown[]) => unknown)(resolvedKeys, ...rest)
    }) as typeof originalTFunction,
    [variants, originalTFunction, i18n]
  )

  return useMemo(
    () => ({
      t: enrichedT,
      i18n,
      ready,
    }),
    [enrichedT, i18n, ready]
  )
}

export const serverSideTranslations = async (
  initialLocale: string,
  namespacesRequired: string[],
  configOverride?: UserConfig | null,
  extraLocales?: string[] | false
): Promise<SSRConfig> => {
  const convergenceNameSpaces = generateI18nNamespace(namespacesRequired)
  return await originalServerSideTranslations(initialLocale, convergenceNameSpaces, configOverride, extraLocales)
}

export const updateTheme = async (data: SetThemeRequest): Promise<void> => {
  try {
    setCookie(null, THEME_COOKIE_NAME, data.theme, {
      // maxAge: 30 * 24 * 60 * 60, // 30 days
      // maxAge: 6 * 60 * 60, // 6 hours
      path: '/',
      // httpOnly: true, // cater for payment schedule widget
      secure: true,
      sameSite: 'none',
      // domain // cater for payment schedule widget
    })
  } catch (error) {
    console.error('Failed to set theme')
  }
}

export const isValidThemeValue = (value?: string): value is Theme => !isUndefinedOrEmpty(value) && Object.values(Theme).includes(value as Theme)

/**
 * Finds the closest shared parent domain between two hosts (e.g. portal.afterpay.com
 * and credit-application.afterpay.com share afterpay.com). Returns undefined when the
 * hosts are identical (host-only cookie suffices) or share fewer than two labels, so a
 * cookie is never scoped to a bare TLD.
 */
export const getSharedCookieDomain = (currentHost: string, targetHost: string): string | undefined => {
  if (currentHost === targetHost) {
    return undefined
  }

  const current = currentHost.split('.')
  const target = targetHost.split('.')
  const shared: string[] = []

  while (current.length > 0 && target.length > 0 && current[current.length - 1] === target[target.length - 1]) {
    shared.unshift(current.pop() as string)
    target.pop()
  }

  return shared.length >= 2 ? shared.join('.') : undefined
}

/**
 * Pre-configures the theme for another Afterpay app before redirecting to it.
 * Sibling apps (e.g. credit-application) are passive theme consumers: they read the
 * `x-apt-theme` cookie and fall back to their own default when it is absent. Since the
 * apps live on different subdomains, the cookie is scoped to the closest shared parent
 * domain so the target app can read it.
 */
export const setThemeHandoffCookie = (theme: Theme, targetUrl: string): void => {
  try {
    const domain = getSharedCookieDomain(window.location.hostname, new URL(targetUrl).hostname)
    setCookie(null, THEME_COOKIE_NAME, theme, {
      path: '/',
      secure: true,
      sameSite: 'none',
      ...(domain !== undefined && { domain }),
    })
  } catch (error) {
    console.error('Failed to set theme handoff cookie')
  }
}

export const useSubHeadingColor = (): ColorTypes => {
  const isAPTheme = useIsTheme(Theme.AP)

  return isAPTheme ? 'Black' : 'Gray40'
}

export const isValidCohortValue = (value?: string): value is Cohort => !isUndefinedOrEmpty(value) && Object.values(Cohort).includes(value as Cohort)

export const useUpdateAfterpayJsBrand = (): void => {
  const [theme] = useCheckoutTheme()
  const { initial } = useCheckoutState()
  const { updateBrand } = useMerchantCallback()

  useEffect(() => {
    if (initial.isWindowed) {
      updateBrand(theme)?.catch(() => {})
    }
  }, [theme])
}

/**
 *  Determines if a consumer uuid is a shadow account or not.
 *  @param {string} consumerUuid
 *  @returns {boolean} Returns `true` if is a shadow account.
 */

export const isShadowAccount = (consumerUuid: string): boolean => {
  return consumerUuid.startsWith('C_')
}

type TransProps = Omit<NextTransProps<string>, 'i18nKey' | 'context' | 'defaults' | 'tOptions'> & {
  i18nKey: string | string[]
  context?: string
  defaults?: string
  tOptions?: (TOptions & { legal?: boolean }) | Record<string, unknown>
}

export const Trans = ({ i18nKey, tOptions, ...otherProps }: TransProps): React.ReactElement => {
  const variants = useActiveCopyVariants()
  const isLegal = typeof tOptions === 'object' && tOptions !== null && 'legal' in tOptions && tOptions.legal === true

  // If legal: true is set, prepend the legal prefix to the key(s)
  const applyLegalPrefix = (key: string): string => (isLegal ? `${LEGAL_PREFIX}${key}` : key)
  const keyWithLegalPrefix = Array.isArray(i18nKey) ? i18nKey.map(applyLegalPrefix) : applyLegalPrefix(i18nKey)

  const convergenceKeys = getTranslationKeys(keyWithLegalPrefix, variants)

  return <NextTrans {...otherProps} i18nKey={convergenceKeys} tOptions={tOptions} />
}
